import { Injectable } from '@angular/core';
import { DateUtilityService } from './date-utility.service';
import { ExerciseDataService } from './exercise-data.service';
import { FormInputService } from './form-input.service';
import { ExerciseSet } from '../models/exercise-set.interface';

export interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: string;
  allotmentId?: string;
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown';
  isMarkedForDeletion?: boolean;
}

export interface DateItem {
  date: Date;
  formattedDate: string;
  exercises: Exercise[];
  isRestDay?: boolean;
  restDayAllotmentId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataPopulationService {
  constructor(
    private dateUtilityService: DateUtilityService,
    private exerciseDataService: ExerciseDataService,
    private formInputService: FormInputService
  ) {}

  /**
   * Populates existing data from allotments and sets date range if needed
   */
  populateExistingData(
    existingAllotments: any[],
    startDate: Date | null,
    endDate: Date | null,
    onEndDateChange: () => void,
    generateDateList: () => void,
    populateExerciseDataCallback: () => void
  ): { startDate: Date | null; endDate: Date | null } {
    if (!existingAllotments || existingAllotments.length === 0) {
      console.log('No existing allotments to populate');
      return { startDate, endDate };
    }

    console.log(
      'Populating existing data from allotments:',
      existingAllotments.length
    );
    console.log('Sample allotment:', existingAllotments[0]);

    // Check if user has already selected a date range manually
    const userHasSelectedDates = startDate && endDate;
    let newStartDate = startDate;
    let newEndDate = endDate;

    if (!userHasSelectedDates) {
      // Only auto-set date range if user hasn't selected dates manually
      console.log('No user-selected dates, auto-setting from existing data');

      // Find the date range from existing allotments
      const dates = existingAllotments
        .map((allotment) => {
          console.log(`Processing allotment date: ${allotment.date}`);
          const parsedDate = this.dateUtilityService.parseAPIDate(
            allotment.date
          );
          console.log(`Parsed date result:`, parsedDate);
          return parsedDate;
        })
        .filter((date) => date !== null) as Date[];

      if (dates.length === 0) {
        console.log('No valid dates found in allotments');
        return { startDate, endDate };
      }

      dates.sort((a, b) => a.getTime() - b.getTime());
      newStartDate = dates[0];
      newEndDate = dates[dates.length - 1];

      console.log(
        'Set date range from existing data:',
        newStartDate,
        'to',
        newEndDate
      );

      // Calculate duration
      onEndDateChange();

      // Generate date list with existing data
      generateDateList();
    } else {
      console.log(
        'User has selected dates manually, using existing date range:',
        startDate,
        'to',
        endDate
      );
      // User has selected dates manually, so we need to regenerate the date list
      // to ensure it includes the user's selected range, then populate existing data
      generateDateList();
    }

    // Populate exercise data for the date range (whether auto-set or user-selected)
    populateExerciseDataCallback();

    return { startDate: newStartDate, endDate: newEndDate };
  }

  /**
   * Populates exercise data from existing allotments
   */
  populateExerciseData(
    dateList: DateItem[],
    existingAllotments: any[],
    availableExercisesData: any[],
    exerciseDataService: any
  ): void {
    console.log('Populating exercise data from existing allotments');
    console.log('Available allotments:', existingAllotments.length);
    console.log('Generated date list length:', dateList.length);

    dateList.forEach((dateItem, index) => {
      const dateString = this.dateUtilityService.formatDateForAPI(
        dateItem.date
      );
      console.log(
        `Processing date ${index + 1}/${dateList.length}:`,
        dateString
      );
      console.log(`  Initial exercises count: ${dateItem.exercises.length}`);

      // Find allotments for this date
      const dayAllotments = existingAllotments.filter((allotment) => {
        const allotmentDateString =
          this.dateUtilityService.formatISODateForComparison(allotment.date);
        const match = allotmentDateString === dateString;

        // Debug logging to see why dates aren't matching
        console.log(`Comparing dates for ${dateString}:`);
        console.log(`  Original allotment.date: ${allotment.date}`);
        console.log(`  Formatted allotment date: ${allotmentDateString}`);
        console.log(`  Generated date string: ${dateString}`);
        console.log(`  Match result: ${match}`);

        if (match) {
          console.log(
            'Found matching allotment for',
            dateString,
            ':',
            allotment
          );
        }
        return match;
      });

      if (dayAllotments.length === 0) {
        console.log(
          'No allotments found for date:',
          dateString,
          '- keeping default exercises'
        );
        return;
      }

      console.log(`Found ${dayAllotments.length} allotments for ${dateString}`);

      // Check if this is a rest day (has allotment with phase "restday")
      const restDayAllotment = dayAllotments.find(
        (allotment) =>
          allotment.phase === 'restday' || allotment.exerciseId === ''
      );

      if (restDayAllotment) {
        console.log(
          'Found rest day allotment for',
          dateString,
          'with ID:',
          restDayAllotment.id
        );
        dateItem.isRestDay = true;
        dateItem.restDayAllotmentId = restDayAllotment.id;
        dateItem.exercises = [];
        return;
      }

      // Clear default exercises only if we have existing exercise data to populate
      dateItem.exercises = [];
      dateItem.isRestDay = false;

      // Group allotments by exercise, section, and allotment ID to prevent merging separate exercises
      const exerciseGroups = new Map<string, any[]>();

      dayAllotments.forEach((allotment) => {
        // Skip rest day allotments when processing exercises
        if (allotment.phase === 'restday' || allotment.exerciseId === '') {
          return;
        }

        // Use allotment ID to ensure each allotment creates a separate exercise
        const key = `${allotment.exerciseId}-${allotment.phase || 'main'}-${
          allotment.id
        }`;
        if (!exerciseGroups.has(key)) {
          exerciseGroups.set(key, []);
        }
        exerciseGroups.get(key)!.push(allotment);
      });

      let exerciseId = 1;
      const sectionsWithData = new Set<'warmup' | 'main' | 'cooldown'>();

      exerciseGroups.forEach((allotments, key) => {
        const firstAllotment = allotments[0];
        console.log('Processing exercise group:', key);
        console.log('First allotment structure:', firstAllotment);

        // Find exercise name by ID
        const exerciseData = availableExercisesData.find(
          (ex) => ex.id === firstAllotment.exerciseId
        );
        const exerciseName = exerciseData
          ? exerciseData.name
          : `Exercise ${firstAllotment.exerciseId}`;

        console.log('Found exercise name:', exerciseName);

        // Sort allotments by sequence number
        allotments.sort((a, b) => (a.seq || 0) - (b.seq || 0));

        // Create sets from allotments - handle the nested sets structure
        const sets = this.createSetsFromAllotments(
          allotments,
          exerciseDataService
        );

        const exercise: Exercise = {
          id: exerciseId++,
          exercise: exerciseName,
          exerciseId: firstAllotment.exerciseId.toString(),
          allotmentId: firstAllotment.id.toString(),
          sets: sets,
          section: this.exerciseDataService.mapPhaseToSection(
            firstAllotment.phase
          ),
        };

        // Track which section has data
        sectionsWithData.add(exercise.section || 'main');

        dateItem.exercises.push(exercise);
        console.log('Added exercise to date:', exercise);
      });

      // Add default empty exercises for sections that don't have existing data
      this.addDefaultEmptyExercises(dateItem, sectionsWithData, exerciseId);

      // Sort exercises by section order (warmup, main, cooldown)
      this.sortExercisesBySection(dateItem.exercises);

      console.log(
        `Final exercises count for ${dateString}: ${dateItem.exercises.length}`
      );
    });

    // Log summary of all dates
    console.log('=== Date Population Summary ===');
    dateList.forEach((dateItem, index) => {
      console.log(
        `Date ${index + 1}: ${this.dateUtilityService.formatDateForAPI(
          dateItem.date
        )} - ${dateItem.exercises.length} exercises`
      );
    });
  }
  /**
   * Applies the exercises from a given dateItem to all upcoming same weekdays in the dateList.
   * Used when user wants to copy changes to all such days in the plan.
   * @param sourceDateItem The dateItem whose exercises should be copied
   * @param dateList The full list of DateItem objects in the plan
   */
  applyExercisesToUpcomingWeekdays(
    sourceDateItem: DateItem,
    dateList: DateItem[]
  ): void {
    if (!sourceDateItem || !sourceDateItem.date || !Array.isArray(dateList))
      return;
    const sourceWeekday = sourceDateItem.date.getDay();
    // Deep copy exercises to avoid reference issues
    const copiedExercises = sourceDateItem.exercises.map((ex) =>
      JSON.parse(JSON.stringify(ex))
    );
    dateList.forEach((item) => {
      if (
        item !== sourceDateItem &&
        item.date.getDay() === sourceWeekday &&
        item.date > sourceDateItem.date // Only future dates
      ) {
        // Only overwrite if not a rest day
        if (!item.isRestDay) {
          // Assign new deep copies for each item
          item.exercises = copiedExercises.map((ex) =>
            JSON.parse(JSON.stringify(ex))
          );
        }
      }
    });
  }

  /**
   * Creates exercise sets from allotment data
   */
  private createSetsFromAllotments(
    allotments: any[],
    exerciseDataService: any
  ): ExerciseSet[] {
    console.log('Creating sets from allotments:', allotments);
    const sets: ExerciseSet[] = [];

    allotments.forEach((allotment, allotmentIndex) => {
      console.log(`Processing allotment ${allotmentIndex + 1}:`, allotment);

      if (allotment.sets && Array.isArray(allotment.sets)) {
        // Process each set within the allotment
        allotment.sets.forEach((setData: any, setIndex: number) => {
          console.log(`    Processing set ${setIndex + 1}:`, setData);

          const set: ExerciseSet = {
            setNumber: setData.seq || sets.length + 1,
            reps: setData.reps ? setData.reps.toString() : '',
            time: exerciseDataService.convertSecondsToTimeString(
              setData.time || 0
            ),
            weights: setData.weights ? setData.weights.toString() : '',
            status:
              typeof setData.status === 'string' && setData.status.trim() !== ''
                ? setData.status
                : 'new',
          };

          // Parse the time string into separate minutes and seconds fields
          this.formInputService.parseSeparateTimeFields(set);

          sets.push(set);
          console.log(`      Created set:`, set);
        });
      } else {
        // Fallback: create a single set from allotment properties (old format support)
        console.log(
          `    No sets array found, creating single set from allotment properties`
        );
        const set: ExerciseSet = {
          setNumber: sets.length + 1,
          reps: allotment.reps ? allotment.reps.toString() : '',
          time: exerciseDataService.convertSecondsToTimeString(
            allotment.time || 0
          ),
          weights: allotment.weights ? allotment.weights.toString() : '',
          status:
            typeof allotment.status === 'string' &&
            allotment.status.trim() !== ''
              ? allotment.status
              : allotment.status !== undefined &&
                allotment.status !== null &&
                String(allotment.status).trim() !== ''
              ? String(allotment.status)
              : 'new',
        };

        // Parse the time string into separate minutes and seconds fields
        this.formInputService.parseSeparateTimeFields(set);

        sets.push(set);
        console.log(`    Created fallback set:`, set);
      }
    });

    // Sort sets by setNumber to ensure proper order
    sets.sort((a, b) => a.setNumber - b.setNumber);
    console.log(`  Final sets array (${sets.length} sets):`, sets);

    return sets;
  }

  /**
   * Adds default empty exercises for sections that don't have existing data
   */
  private addDefaultEmptyExercises(
    dateItem: DateItem,
    sectionsWithData: Set<'warmup' | 'main' | 'cooldown'>,
    exerciseId: number
  ): void {
    const allSections: Array<'warmup' | 'main' | 'cooldown'> = [
      'warmup',
      'main',
      'cooldown',
    ];

    allSections.forEach((section) => {
      if (!sectionsWithData.has(section)) {
        console.log(`Adding default empty exercise for section: ${section}`);
        const defaultExercise: Exercise = {
          id: exerciseId++,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: section,
        };
        dateItem.exercises.push(defaultExercise);
      }
    });
  }

  /**
   * Sorts exercises by section order (warmup, main, cooldown)
   */
  private sortExercisesBySection(exercises: Exercise[]): void {
    const sectionOrder = { warmup: 1, main: 2, cooldown: 3 };
    exercises.sort(
      (a, b) =>
        sectionOrder[a.section || 'main'] - sectionOrder[b.section || 'main']
    );
  }
}
